// Workspaces, and who is in them.
//
// The unit of ownership: every slide belongs to one, and sharing a workspace is
// how anyone else sees it. This page is the whole of that — the API has existed
// since the tenancy work and until now was reachable only with curl.
//
// Every workspace here is an ordinary workspace, INCLUDING the one an account
// started with. It can be shared, renamed, handed over, or deleted. 'Personal'
// is a label for how it was created and confers nothing.
//
// Every workspace has an OWNER (server 0093): whoever made it, until they hand
// it to another administrator. Its storage is theirs, so only they hand it
// over or delete it, and they stay an administrator and a member until then.
//
// That used not to be true, and the difference is where uploads go. It was a
// property of the workspace — recognised by a slug naming its owner — so
// sharing one meant a co-administrator could remove the owner from the only
// place their uploads could land, unrepairably. It is a pointer on the account
// now: exactly one workspace is the DEFAULT, the person moves it, and being
// removed from a workspace costs them that workspace and nothing more.
//
// Which is why every row carries that choice, in the actions on the right,
// ahead of Members: exactly one workspace says Default and every other offers
// Make default, in the same slot, so nothing moves between rows.
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Trash2, Upload, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { currentSession } from '../../auth/passwordSession';
import {
  addMember,
  createWorkspace,
  deleteWorkspace,
  listMembers,
  listWorkspaces,
  removeMember,
  renameWorkspace,
  revokeInvitation,
  setDefaultWorkspace,
  setMemberRole,
  transferOwnership,
  ROLES,
  ROLE_HINT,
  ROLE_LABEL,
  type Invitation,
  type Member,
  type Role,
  type Workspace,
} from '../../features/workspaces/api';

export function WorkspacesPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['workspaces'],
    queryFn: listWorkspaces,
  });
  const [creating, setCreating] = useState(false);
  // ?new=1 is "New workspace" in the workspace menu: open the dialog, once.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('new') !== '1') return;
    setCreating(true);
    setParams((p) => {
      p.delete('new');
      return p;
    }, { replace: true });
  }, [params, setParams]);
  const [open, setOpen] = useState<Workspace | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['workspaces'] });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Workspaces</h1>
          <p className="text-sm text-muted-foreground">
            Slides belong to a workspace. Share one to work on them together.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          New workspace
        </Button>
      </div>

      {error && <Problem>{(error as Error).message}</Problem>}
      {isLoading && <Skeleton className="h-24 w-full" />}

      {/* The state the server refuses uploads in, said here where it can be
          fixed. Two different sentences because there are two different next
          actions: pick one of the workspaces below, or make one first. */}
      {data && !data.workspaces.some((w) => w.is_default) && (
        <div
          role="status"
          className="mb-4 flex items-start gap-2.5 rounded-md border border-primary/30 bg-primary/5 px-3.5 py-3 text-sm"
        >
          <Upload className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-foreground">
            {/* Rows being deleted are listed, and they are not candidates —
                nothing can upload into one. Counting them would offer a choice
                with nothing behind it, which is the mistake the server made in
                the mirror image of this test. */}
            {data.workspaces.some((w) => w.role !== 'viewer' && !w.archived)
              ? 'Your uploads have nowhere to go. Pick one below with “Make default”.'
              : 'Your uploads have nowhere to go. Create a workspace to put them in.'}
          </p>
        </div>
      )}

      {data && (
        <div className="divide-y rounded-md border">
          {data.workspaces.map((w) => (
            <Row key={w.public_id} workspace={w} onManage={() => setOpen(w)} onChanged={refresh} />
          ))}
        </div>
      )}

      {creating && <CreateDialog onClose={() => setCreating(false)} onCreated={refresh} />}
      {open && (
        <MembersDialog
          workspace={open}
          onClose={() => {
            setOpen(null);
            // Member counts live on the list, so it is stale the moment this
            // dialog changes anything.
            refresh();
          }}
        />
      )}
    </div>
  );
}

function Problem({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p role="alert" className="mb-4 text-sm text-destructive">
      {children}
    </p>
  );
}

function Row({
  workspace,
  onManage,
  onChanged,
}: {
  workspace: Workspace;
  onManage: () => void;
  onChanged: () => void;
}): JSX.Element {
  // A workspace being deleted grants nothing, so every control that acts ON its
  // contents is withheld — the server refuses them all anyway. What is left is
  // its name, so it can be recognised, and Delete, which starts the teardown
  // again (it converges) for one archived before deleting replaced archiving.
  if (workspace.archived) {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-muted-foreground line-through">
            {workspace.name}
          </div>
          <div className="text-xs text-muted-foreground">
            Being deleted · nobody can reach it
          </div>
        </div>
        {workspace.is_owner && <DeleteWorkspaceButton workspace={workspace} onDeleted={onChanged} />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{workspace.name}</div>
        <div className="text-xs text-muted-foreground">
          {ROLE_LABEL[workspace.role]}
          {workspace.is_owner ? ' · Owner' : ''} · {workspace.members}{' '}
          {workspace.members === 1 ? 'member' : 'members'}
        </div>
      </div>
      {/* Offered on every workspace, because every workspace is shareable now.
          The built-in one is the only exception and no user is a member of it. */}
      <DefaultControl workspace={workspace} onChanged={onChanged} />
      <Button variant="outline" size="sm" onClick={onManage} className="gap-1.5">
        <Users className="h-4 w-4" />
        Members
      </Button>
      {/* Only its owner deletes it: it is their storage (server 0093). */}
      {workspace.is_owner && <DeleteWorkspaceButton workspace={workspace} onDeleted={onChanged} />}
    </div>
  );
}

/**
 * Which workspace this account's slides go to — ONE slot, always in the same
 * place: the first of the row's actions, ahead of Members.
 *
 * It used to be two things in two places: a badge beside the name of the chosen
 * workspace, and a button over in the actions on every other row. So the thing
 * you were looking for moved from one side of the row to the other depending on
 * which row it was, which is how you lose track of a single-select.
 *
 * The slot keeps a fixed width so "Default" and "Make default" occupy the same
 * space, and Members and Delete line up down the list instead of shuffling
 * left and right by a few pixels per row.
 *
 * Called "Default" rather than "Uploads" because that is what it is: the
 * workspace this account falls back to when nothing else says otherwise.
 */
function DefaultControl({
  workspace,
  onChanged,
}: {
  workspace: Workspace;
  onChanged: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => setDefaultWorkspace(workspace.public_id),
    onSuccess: () => {
      onChanged();
      // The session carries the "you have nowhere to upload" refusal and is
      // cached with staleTime: Infinity, so without this the upload page would
      // go on saying there is nowhere to put files for the rest of the session
      // — after the person had just fixed it here.
      void qc.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });

  // A viewer may read this workspace but not write to it, and the server
  // refuses to point their uploads here. The slot stays, empty, so the row
  // still lines up with the ones that have a control in it.
  const allowed = workspace.role !== 'viewer';

  return (
    <div className="flex w-32 shrink-0 justify-end">
      {workspace.is_default ? (
        <span
          className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary"
          title="This account's uploads go here"
        >
          <Check className="h-3.5 w-3.5" />
          Default
        </span>
      ) : allowed ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={m.isPending}
          onClick={() => m.mutate()}
          title={`Send this account's uploads to ${workspace.name}`}
          className="text-muted-foreground"
        >
          {m.isPending ? 'Setting…' : 'Make default'}
        </Button>
      ) : null}
      {m.error && (
        <span role="alert" className="ml-2 self-center text-xs text-destructive">
          {(m.error as Error).message}
        </span>
      )}
    </div>
  );
}

/**
 * Delete, behind a confirmation that names what goes and asks for the name.
 *
 * Nothing comes back: every slide, upload, annotation, collection, comment and
 * share link in it, then the workspace and its members. Typing the name is the
 * one thing a misclick cannot do.
 */
function DeleteWorkspaceButton({
  workspace,
  onDeleted,
}: {
  workspace: Workspace;
  onDeleted: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [confirming, setConfirmingState] = useState(false);
  const [typed, setTyped] = useState('');
  // Opening or closing always starts from an empty box: a name typed before a
  // Cancel must not leave the next open one click from deleting everything.
  const setConfirming = (open: boolean) => {
    setTyped('');
    setConfirmingState(open);
  };
  const mutation = useMutation({
    mutationFn: () => deleteWorkspace(workspace.public_id, typed),
    onSuccess: () => {
      setConfirming(false);
      onDeleted();
      // Its slides leave every list at once, and the session says where uploads go.
      void qc.invalidateQueries();
    },
  });
  const matches = typed.trim() === workspace.name.trim();

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setConfirming(true)}
        title="Delete this workspace and everything in it"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      {confirming && (
        <Dialog open onOpenChange={(o) => !o && setConfirming(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete “{workspace.name}”?</DialogTitle>
              <DialogDescription>
                Everything in it is deleted for good: every slide and upload, their annotations,
                every collection, comment and share link, then the workspace and its members. This
                cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <label className="flex flex-col gap-1.5 text-sm">
              <span>Type the workspace name to confirm</span>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={workspace.name} autoFocus />
            </label>
            {mutation.error && <Problem>{(mutation.error as Error).message}</Problem>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => mutation.mutate()}
                disabled={!matches || mutation.isPending}
              >
                {mutation.isPending ? 'Deleting…' : 'Delete workspace'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function CreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const mutation = useMutation({
    mutationFn: () => createWorkspace(name.trim()),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!mutation.isPending && name.trim()) mutation.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>New workspace</DialogTitle>
            <DialogDescription>
              You administer it, and can add people once it exists.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Thyroid study group"
            autoFocus
            maxLength={200}
            disabled={mutation.isPending}
          />
          {mutation.error && <Problem>{(mutation.error as Error).message}</Problem>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !name.trim()}>
              {mutation.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({
  workspace,
  onClose,
}: {
  workspace: Workspace;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const key = ['workspace-members', workspace.public_id];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => listMembers(workspace.public_id) });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });

  /**
   * The caller's role read from THIS list, not from the workspace row.
   *
   * The row came from the workspaces query, which is not refetched while this
   * dialog is open — so demoting yourself left the admin controls on screen,
   * offering actions the server would then refuse. The member list is fetched
   * here and invalidated on every change, so the caller's own entry in it is
   * the freshest answer available, and it self-corrects the moment they change
   * their own role. Falls back to the row while the list is still loading.
   */
  const session = useQuery({ queryKey: ['auth', 'session'], queryFn: currentSession, staleTime: Infinity, retry: false });
  const mine = data?.members.find((m) => m.user_id === session.data?.userId);
  const admin = (mine?.role ?? workspace.role) === 'workspace_admin';
  // Read the same way, so the hand-over controls go the moment it is handed over.
  const owner = mine?.is_owner ?? workspace.is_owner;
  // Inviting somebody who has no account is a deployment administrator's act
  // on enterprise (the server refuses it for anyone else); a workspace
  // administrator adds people who already exist. Said in the form rather than
  // discovered from a 403 after typing an address.
  const canInvite = session.data?.superadmin === true;

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  // What the server did, so the form can say it. Adding an existing account and
  // inviting a stranger are one action to the person typing an address and two
  // different outcomes worth distinguishing: one of them means "go and tell
  // them", the other means "a link is on its way".
  const [invited, setInvited] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => addMember(workspace.public_id, email.trim(), role),
    onSuccess: (result) => {
      setInvited('invited' in result ? result.email : null);
      setEmail('');
      refresh();
    },
  });
  // One error slot for the whole member list: only one row can be acted on at a
  // time, and a failure that scrolled off with its row would be a failure
  // nobody saw.
  const [rowError, setRowError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{workspace.name}</DialogTitle>
          <DialogDescription>
            {admin
              ? 'Everyone here can open its slides. Administrators can also change who is in it.'
              : 'Everyone here can open its slides.'}
          </DialogDescription>
        </DialogHeader>

        {admin && <RenameField workspace={workspace} onRenamed={onClose} />}

        {isLoading && <Skeleton className="h-20 w-full" />}
        {data && (
          <div className="divide-y rounded-md border">
            {data.members.map((m) => (
              <MemberRow
                key={m.user_id}
                workspace={workspace}
                member={m}
                canEdit={admin}
                // By the owner flag, not by comparing ids: the session's id is
                // the issuer's subject, which under OIDC is not a member id.
                canHandOver={owner && !m.is_owner && m.role === 'workspace_admin'}
                onChanged={() => {
                  refresh();
                  // Who owns it shows on the list behind the dialog too.
                  void qc.invalidateQueries({ queryKey: ['workspaces'] });
                }}
                onError={setRowError}
              />
            ))}
            {/* Below the members, in the same list, because that is what they
                are: people who belong here and have not arrived. Keeping them
                in a separate panel would let you invite somebody twice without
                ever seeing the first invitation. */}
            {data.invitations.map((i) => (
              <InvitationRow
                key={i.public_id}
                workspace={workspace}
                invitation={i}
                canEdit={admin}
                onChanged={refresh}
                onError={setRowError}
              />
            ))}
          </div>
        )}
        {rowError && <Problem>{rowError}</Problem>}

        {admin && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!add.isPending && email.trim()) add.mutate();
            }}
            className="space-y-2 border-t pt-4"
          >
            <label htmlFor="invite" className="text-sm font-medium text-foreground">
              {canInvite ? 'Add or invite someone' : 'Add someone'}
            </label>
            <div className="flex gap-2">
              <Input
                id="invite"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.org"
                disabled={add.isPending}
              />
              <RoleSelect value={role} onChange={setRole} disabled={add.isPending} label="Role for the person you are adding" />
              <Button type="submit" disabled={add.isPending || !email.trim()}>
                {add.isPending ? 'Sending…' : 'Add'}
              </Button>
            </div>
            {/* What the chosen role actually grants, from the one table both
                shells read. The picker named three roles and explained none of
                them, so the difference between Viewer and Editor had to be
                discovered by watching what a colleague could not do. */}
            <p className="text-xs text-muted-foreground">{ROLE_HINT[role]}</p>
            <p className="text-xs text-muted-foreground">
              {canInvite
                ? 'If they have no account here, we will email them an invitation.'
                : 'They need an account here already. Only a deployment administrator can invite someone who has none.'}
            </p>
            {/* Said only when it happened. Adding somebody who is already here
                and inviting somebody who is not are one action to whoever typed
                the address, and two outcomes worth telling apart: one means
                they can open it now, the other means a link is on its way and
                nothing has happened yet. */}
            {invited && (
              <p role="status" className="text-xs text-primary">
                Invitation sent to {invited}. They will appear above until they join.
              </p>
            )}
            {add.error && <Problem>{(add.error as Error).message}</Problem>}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Somebody invited who has not arrived.
 *
 * Rendered in the member list, muted, so the count you see matches the people
 * who will eventually be here — and so inviting the same person twice is
 * visibly redundant rather than silently allowed.
 */
function InvitationRow({
  workspace,
  invitation,
  canEdit,
  onChanged,
  onError,
}: {
  workspace: Workspace;
  invitation: Invitation;
  canEdit: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}): JSX.Element {
  const m = useMutation({
    mutationFn: () => revokeInvitation(workspace.public_id, invitation.public_id),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (e) => onError((e as Error).message),
  });
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm text-muted-foreground">{invitation.email}</span>
          <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            Invited
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {ROLE_LABEL[invitation.role]} once they join
        </div>
      </div>
      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          disabled={m.isPending}
          onClick={() => m.mutate()}
          title="Withdraw this invitation"
          className="text-muted-foreground"
        >
          {m.isPending ? 'Withdrawing…' : 'Withdraw'}
        </Button>
      )}
    </div>
  );
}

function MemberRow({
  workspace,
  member,
  canEdit,
  canHandOver,
  onChanged,
  onError,
}: {
  workspace: Workspace;
  member: Member;
  canEdit: boolean;
  /** The caller owns the workspace and this is another administrator: they may be made its owner. */
  canHandOver: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}): JSX.Element {
  const change = useMutation({
    mutationFn: (role: Role) => setMemberRole(workspace.public_id, member.user_id, role),
    onMutate: () => onError(null),
    onSuccess: onChanged,
    onError: (e: Error) => onError(e.message),
  });
  const remove = useMutation({
    mutationFn: () => removeMember(workspace.public_id, member.user_id),
    onMutate: () => onError(null),
    onSuccess: onChanged,
    onError: (e: Error) => onError(e.message),
  });
  const handOver = useMutation({
    mutationFn: () => transferOwnership(workspace.public_id, member.user_id),
    onMutate: () => onError(null),
    onSuccess: () => {
      setConfirming(false);
      onChanged();
    },
    onError: (e: Error) => {
      setConfirming(false);
      onError(e.message);
    },
  });
  const [confirming, setConfirming] = useState(false);
  const busy = change.isPending || remove.isPending || handOver.isPending;

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm text-foreground">{member.display_name}</span>
          {member.is_owner && (
            <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              Owner
            </span>
          )}
        </div>
        {member.email && member.email !== member.display_name && (
          <div className="truncate text-xs text-muted-foreground">{member.email}</div>
        )}
      </div>
      {canHandOver && (
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(true)} className="text-muted-foreground">
          Make owner
        </Button>
      )}
      {confirming && (
        <Dialog open onOpenChange={(o) => !o && setConfirming(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Make {member.display_name} the owner of “{workspace.name}”?</DialogTitle>
              <DialogDescription>
                Its storage will count against theirs instead of yours. You stay an administrator; only they can hand
                it on, or delete it, after this.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button onClick={() => handOver.mutate()} disabled={handOver.isPending}>
                {handOver.isPending ? 'Handing over…' : 'Make owner'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* The owner stays an administrator and a member until they hand the
          workspace over, so their role is shown, not offered. */}
      {canEdit && !member.is_owner ? (
        <>
          <RoleSelect
            value={member.role}
            onChange={(r) => change.mutate(r)}
            disabled={busy}
            label={`Role for ${member.display_name}`}
          />
          {/* The last administrator cannot be demoted or removed — enforced in
              the mutating statement itself, so the button stays live and the
              server explains the refusal. Deciding it here would mean guessing
              from a member list that may already be stale. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => remove.mutate()}
            disabled={busy}
            title="Remove from this workspace"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <span className="text-xs text-muted-foreground">{ROLE_LABEL[member.role]}</span>
      )}
    </div>
  );
}

function RoleSelect({
  value,
  onChange,
  disabled,
  label,
}: {
  value: Role;
  onChange: (r: Role) => void;
  disabled?: boolean;
  /** Named for a screen reader: several of these sit in a list and read
   *  identically without it, so which person a role belongs to is lost. */
  label: string;
}): JSX.Element {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as Role)}
      disabled={disabled}
      className="h-9 rounded-md border border-input bg-transparent px-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
    >
      {ROLES.map((r) => (
        <option key={r} value={r} className="bg-background">
          {ROLE_LABEL[r]}
        </option>
      ))}
    </select>
  );
}

/**
 * Rename, saved on blur or Enter rather than behind its own button.
 *
 * Closes the dialog on success, because the name is shown in its title and in
 * the row behind it, and both are read from a list this does not refresh. The
 * alternative is a dialog headed with the old name while the field holds the
 * new one.
 */
function RenameField({
  workspace,
  onRenamed,
}: {
  workspace: Workspace;
  onRenamed: () => void;
}): JSX.Element {
  const [name, setName] = useState(workspace.name);
  const [error, setError] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: () => renameWorkspace(workspace.public_id, name.trim()),
    onMutate: () => setError(null),
    onSuccess: onRenamed,
    onError: (e: Error) => setError(e.message),
  });
  const changed = name.trim() && name.trim() !== workspace.name;
  const save = () => {
    if (changed && !rename.isPending) rename.mutate();
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor="ws-name" className="text-sm font-medium text-foreground">
        Name
      </label>
      <Input
        id="ws-name"
        value={name}
        maxLength={200}
        disabled={rename.isPending}
        onChange={(e) => setName(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
          // Escape puts it back, so a half-typed name is not saved by a stray
          // click landing outside the field.
          if (e.key === 'Escape') setName(workspace.name);
        }}
      />
      {error && <Problem>{error}</Problem>}
    </div>
  );
}
